import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getUser } from 'redux/selectors';
import { updateUI } from 'redux/actions/ui';
import buildAuthorization from 'common/utils/buildAuthorization';
import settings from 'common/configs/setting';
import useS3MultipartUpload from 'hooks/useS3MultipartUpload';
import './BulkImageUploadDialog.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB

const getApiBaseUrl = () => {
    const stored = localStorage.getItem('API_URL') || '';
    return stored.replace(/\/+$/, '') + settings.api;
};

const getApiV2BaseUrl = () => {
    const baseUrl = getApiBaseUrl();
    if (baseUrl.includes('/v1')) {
        return baseUrl.replace('/v1', '/v2');
    }
    return baseUrl;
};

const STATUS = {
    IDLE: 'idle',
    GETTING_URL: 'getting_url',
    UPLOADING: 'uploading',
    CONFIRMING: 'confirming',
    POLLING: 'polling',
    DONE: 'done',
    ERROR: 'error'
};

// Display mode for the modal (mirrors ImportGmailDialog pattern)
const DISPLAY = {
    FULL: 'full', // Normal modal visible
    MESSAGE: 'message', // Brief "processing in background" message (2.5s)
    HIDDEN: 'hidden' // Renders null — component alive but invisible (polling continues)
};

const POLL_INTERVAL_MS = 3000;

const BulkImageUploadDialog = ({ close, journal }) => {
    const user = useSelector(getUser);
    const reduxDispatch = useDispatch();
    const fileInputRef = useRef(null);
    const pollTimerRef = useRef(null);
    const messageTimerRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [status, setStatus] = useState(STATUS.IDLE);
    const [errorMsg, setErrorMsg] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [displayMode, setDisplayMode] = useState(DISPLAY.FULL);

    const journalId = journal?.id;
    const authHeader = buildAuthorization(user);
    const isProcessingRef = useRef(false);

    // Use multipart upload for all files
    const {
        uploadToS3Multipart,
        cancelUpload,
        resetUploadState,
        formatBytes,
        formatDuration,
        uploadProgress,
        uploadSpeed,
        uploadEta
    } = useS3MultipartUpload();

    // Safety cleanup on unmount: clear timers and in-progress flag
    useEffect(() => {
        return () => {
            clearInterval(pollTimerRef.current);
            clearTimeout(messageTimerRef.current);
            cancelUpload();
            resetUploadState();
            reduxDispatch(updateUI({ bulkImageImportInProgress: false }));
        };
    }, [reduxDispatch, cancelUpload, resetUploadState]);

    // ── Step 1: Get multipart init config ────────────────────────────────────
    const getMultipartConfig = useCallback(
        async (file) => {
            const params = new URLSearchParams({
                filename: file.name,
                file_size: file.size
            });
            const url = `${getApiV2BaseUrl()}/journal/${journalId}/zip-upload/multipart-init/?${params}`;
            try {
                const res = await fetch(url, {
                    method: 'GET',
                    headers: { Authorization: authHeader }
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(
                        `Could not init multipart upload (HTTP ${res.status}): ${txt}`
                    );
                }
                return await res.json();
            } catch (err) {
                throw new Error(
                    `Network error getting multipart config: ${err.message}`
                );
            }
        },
        [journalId, authHeader]
    );

    // ── Step 2: Complete multipart upload ────────────────────────────────────
    const confirmMultipartUpload = useCallback(
        async (uploadId, s3_key, partETags) => {
            const url = `${getApiV2BaseUrl()}/journal/${journalId}/zip-upload/multipart-complete/`;
            try {
                const res = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        upload_id: uploadId,
                        s3_key,
                        part_etags: partETags
                    })
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(
                        `Multipart confirm failed (HTTP ${res.status}): ${txt}`
                    );
                }
                return await res.json();
            } catch (err) {
                throw new Error(
                    `Exception during multipart confirm: ${err.message}`
                );
            }
        },
        [journalId, authHeader]
    );

    // ── Step 4: poll job ─────────────────────────────────────────────────────
    // When polling starts:
    //   1. Signal sidebar: disable button + show spinner
    //   2. Show brief "Upload successful, processing in background…" message
    //   3. After 2.5s → hide modal (renders null, polling continues)
    //   4. On complete → dispatch result to Redux → sidebar popup → self-close
    const startPolling = useCallback(
        (jobId) => {
            clearInterval(pollTimerRef.current);
            setStatus(STATUS.POLLING);

            // Signal sidebar: disable button + show spinner
            reduxDispatch(
                updateUI({
                    bulkImageImportInProgress: true,
                    bulkImageJobResult: null
                })
            );

            // Store in local storage to allow fetching across refreshes
            localStorage.setItem(
                'activeBulkImageJob',
                JSON.stringify({ jobId, journalId })
            );

            // Show brief success message then background
            setDisplayMode(DISPLAY.MESSAGE);
            messageTimerRef.current = setTimeout(() => {
                setDisplayMode(DISPLAY.HIDDEN);
            }, 2500);

            pollTimerRef.current = setInterval(async () => {
                const statusUrl = `${getApiBaseUrl()}/zip_photo_import_job/${jobId}/`;
                try {
                    const res = await fetch(statusUrl, {
                        method: 'GET',
                        headers: { Authorization: authHeader }
                    });
                    if (!res.ok) {
                        // 404 means job not registered yet — keep polling
                        if (res.status === 404) {
                            return;
                        }
                        throw new Error(`Poll failed (HTTP ${res.status})`);
                    }
                    const data = await res.json();
                    const st = (data.status || '').toUpperCase();

                    if (st === 'DONE' || st === 'SUCCESS' || st === 'FAILED') {
                        clearInterval(pollTimerRef.current);
                        clearTimeout(messageTimerRef.current);

                        const result = {
                            journalId,
                            success: data.success_count ?? 0,
                            skipped: data.skipped_count ?? 0,
                            failed: data.failed_count ?? 0,
                            total: data.total_count ?? 0,
                            isError: st === 'FAILED',
                            errorDetails: data.error_details || '',
                            skippedReasons: data.skipped_reasons || '[]',
                            failedReasons: data.failed_reasons || '[]'
                        };

                        // Push result to Redux for sidebar popup, clear in-progress
                        reduxDispatch(
                            updateUI({
                                bulkImageImportInProgress: false,
                                bulkImageJobResult: result
                            })
                        );

                        // Self-close dialog (polling is done — safe to unmount)
                        if (close) {
                            close();
                        }
                    }
                } catch (err) {
                    clearInterval(pollTimerRef.current);
                    clearTimeout(messageTimerRef.current);
                    reduxDispatch(
                        updateUI({
                            bulkImageImportInProgress: false,
                            bulkImageJobResult: {
                                success: 0,
                                skipped: 0,
                                failed: 0,
                                total: 0,
                                isError: true,
                                errorDetails: err.message || 'Unknown error'
                            }
                        })
                    );
                    if (close) {
                        close();
                    }
                }
            }, POLL_INTERVAL_MS);
        },
        [authHeader, close, reduxDispatch, journalId]
    );

    // ── Main upload flow (auto-triggered when pendingFile is set) ────────────
    useEffect(() => {
        if (!pendingFile) {
            return;
        }

        const fileToUpload = pendingFile;
        setPendingFile(null); // Consume immediately to prevent React dependency re-triggers

        const run = async () => {
            isProcessingRef.current = true;
            if (!journalId) {
                setErrorMsg(
                    'No active journal found. Please close and try again.'
                );
                setStatus(STATUS.ERROR);
                isProcessingRef.current = false;
                return;
            }

            try {
                setStatus(STATUS.GETTING_URL);
                setErrorMsg('');

                // Use multipart upload for all files
                const multipartConfig = await getMultipartConfig(fileToUpload);

                // Upload chunks in parallel with real progress tracking
                setStatus(STATUS.UPLOADING);
                const multipartResult = await uploadToS3Multipart(
                    fileToUpload,
                    multipartConfig
                );

                // Complete the multipart upload
                setStatus(STATUS.CONFIRMING);
                const confirmData = await confirmMultipartUpload(
                    multipartResult.uploadId,
                    multipartResult.s3_key,
                    multipartResult.partETags
                );

                const jobId = confirmData?.id || confirmData?.job_id;
                if (!jobId) {
                    throw new Error('No job ID returned from server.');
                }
                startPolling(jobId);
            } catch (err) {
                setErrorMsg(err.message || 'Upload failed. Please try again.');
                setStatus(STATUS.ERROR);
            } finally {
                isProcessingRef.current = false;
            }
        };

        run();
    }, [
        pendingFile,
        journalId,
        getMultipartConfig,
        uploadToS3Multipart,
        confirmMultipartUpload,
        startPolling
    ]);

    // ── File Selection Handlers ─────────────────────────────────────────────
    const handleFile = useCallback((file) => {
        if (!file || isProcessingRef.current) {
            return;
        }
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext !== 'zip') {
            setErrorMsg(
                'Unsupported format. Please upload a .zip file containing your images.'
            );
            setStatus(STATUS.ERROR);
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setErrorMsg(
                'File exceeds 10GB limit. Please upload a smaller file.'
            );
            setStatus(STATUS.ERROR);
            return;
        }
        setSelectedFile(file);
        setStatus(STATUS.IDLE);
        setErrorMsg('');
        setPendingFile(file);
    }, []);

    const onSelectFile = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleFileSelect = (e) => handleFile(e.target.files[0]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragging(false), []);

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFile(e.dataTransfer.files[0]);
        },
        [handleFile]
    );

    const onRetry = useCallback(() => {
        if (selectedFile) {
            setStatus(STATUS.IDLE);
            setErrorMsg('');
            setDisplayMode(DISPLAY.FULL);
            setPendingFile(selectedFile);
        }
    }, [selectedFile]);

    const onDone = useCallback(() => {
        clearInterval(pollTimerRef.current);
        clearTimeout(messageTimerRef.current);
        if (close) {
            close();
        }
    }, [close]);

    // ── Render: HIDDEN ────────────────────────────────────────────────────────
    if (displayMode === DISPLAY.HIDDEN) {
        return null;
    }

    // ── Render: MESSAGE — brief "processing in background" overlay ───────────
    if (displayMode === DISPLAY.MESSAGE) {
        return (
            <div
                id="bulk-image-upload-modal"
                className="modal-frame biu-overlay">
                <div className="modal-box biu-modal-box biu-modal-box--message">
                    <div className="biu-bg-message">
                        <div className="biu-bg-message__icon">✓</div>
                        <h3 className="biu-bg-message__title">
                            Upload Successful!
                        </h3>
                        <p className="biu-bg-message__body">
                            Your zip file has been uploaded. Images are now
                            being processed in the background &mdash; you can
                            continue using the app. We&apos;ll notify you here
                            when it&apos;s done.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Render: FULL — normal modal ───────────────────────────────────────────
    const isProcessing = [
        STATUS.GETTING_URL,
        STATUS.UPLOADING,
        STATUS.CONFIRMING
    ].includes(status);

    const stepLabel =
        {
            [STATUS.IDLE]: '',
            [STATUS.GETTING_URL]: 'Preparing secure upload…',
            [STATUS.UPLOADING]: `Uploading file… ${uploadProgress}%`,
            [STATUS.CONFIRMING]: 'Confirming with server…',
            [STATUS.DONE]: '',
            [STATUS.ERROR]: ''
        }[status] || '';

    return (
        <div id="bulk-image-upload-modal" className="modal-frame biu-overlay">
            <div className="modal-box biu-modal-box">
                <div className="biu-header">
                    <h2 className="biu-title">Bulk Image Upload</h2>
                </div>

                <div className="biu-content">
                    <p className="biu-instructions">
                        You can bulk upload images into your Mission JRNL. Only
                        images that fall within 42 months of your mission start
                        date will be accepted. Any images beyond that will be
                        excluded. All images from the same day will be grouped
                        into an individual journal entry. Video, audio files, or
                        other documents or file types will not be included.
                    </p>

                    {/* ── Status Banner ── */}
                    {status === STATUS.ERROR && (
                        <div className="biu-banner biu-banner--error">
                            <span role="img" aria-label="error">
                                ⚠
                            </span>
                            <span>{errorMsg}</span>
                            {selectedFile && (
                                <button
                                    className="biu-banner__retry"
                                    onClick={onRetry}>
                                    Retry
                                </button>
                            )}
                        </div>
                    )}
                    {isProcessing && stepLabel && (
                        <div className="biu-banner biu-banner--info">
                            <div className="biu-spinner" />
                            <span>{stepLabel}</span>
                        </div>
                    )}

                    {/* ── Drop Zone ── */}
                    <div
                        className={`biu-dropzone ${
                            isDragging ? 'biu-dropzone--dragging' : ''
                        } ${isProcessing ? 'biu-dropzone--busy' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept=".zip"
                            onChange={handleFileSelect}
                        />

                        {/* GETTING_URL — preparing */}
                        {status === STATUS.GETTING_URL && (
                            <div className="biu-dropzone-status">
                                <div className="biu-dropzone-status__spinner" />
                                <p className="biu-dropzone-status__label">
                                    Preparing secure upload…
                                </p>
                                <p className="biu-dropzone-status__sub">
                                    Getting a secure upload URL from the server
                                </p>
                            </div>
                        )}

                        {/* UPLOADING — progress bar with speed & ETA */}
                        {status === STATUS.UPLOADING && (
                            <div className="biu-progress-wrap">
                                {/* Progress bar */}
                                <div className="biu-progress-bar">
                                    <div
                                        className="biu-progress-bar__fill"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>

                                {/* Progress percentage */}
                                <div className="biu-progress-header">
                                    <span className="biu-progress-pct">
                                        {uploadProgress}%
                                    </span>
                                    <span className="biu-progress-eta">
                                        {uploadSpeed > 0
                                            ? `${formatDuration(
                                                  uploadEta
                                              )} remaining`
                                            : 'Calculating…'}
                                    </span>
                                </div>

                                {/* Speed & file info */}
                                <div className="biu-progress-stats">
                                    <div className="biu-stat">
                                        <span className="biu-stat__label">
                                            Speed:
                                        </span>
                                        <span className="biu-stat__value">
                                            {uploadSpeed} Mbps
                                        </span>
                                    </div>
                                    {selectedFile && (
                                        <div className="biu-stat">
                                            <span className="biu-stat__label">
                                                File size:
                                            </span>
                                            <span className="biu-stat__value">
                                                {formatBytes(selectedFile.size)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Cancel button */}
                                <button
                                    type="button"
                                    className="biu-btn-cancel"
                                    style={{ pointerEvents: 'auto' }}
                                    onClick={() => {
                                        cancelUpload();
                                        if (close) {
                                            close();
                                        }
                                    }}>
                                    Cancel Upload
                                </button>
                            </div>
                        )}

                        {/* CONFIRMING — waiting for server */}
                        {status === STATUS.CONFIRMING && (
                            <div className="biu-dropzone-status">
                                <div className="biu-dropzone-status__spinner" />
                                <p className="biu-dropzone-status__label">
                                    Confirming with server…
                                </p>
                                <p className="biu-dropzone-status__sub">
                                    Registering the uploaded file for processing
                                </p>
                            </div>
                        )}

                        {!isProcessing && (
                            <>
                                <p className="biu-dropzone-title">
                                    Drop Files Here to Upload
                                </p>

                                {selectedFile && (
                                    <p className="biu-selected-file">
                                        <span role="img" aria-label="file">
                                            📎
                                        </span>{' '}
                                        {selectedFile.name}
                                    </p>
                                )}

                                <button
                                    className="btn btn-gradient biu-select-btn"
                                    onClick={onSelectFile}>
                                    Select File
                                </button>

                                <div className="biu-dropzone-info">
                                    <p>
                                        Please upload a single .zip file
                                        containing your images
                                    </p>
                                    <p>Maximum upload size: 10 GB</p>
                                    <p>
                                        Supported formats inside zip: .jpg,
                                        .jpeg, .png, .gif, .tiff, .tif, .webp,
                                        .heic
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="biu-footer">
                    <button
                        className="btn btn-outline biu-done-btn"
                        onClick={onDone}
                        disabled={isProcessing}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkImageUploadDialog;
